import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import useAuthStore from '../../stores/authStore';

/**
 * Protected route that checks if user is authenticated and has the required permission
 * 
 * @param {Object} props
 * @param {Object} props.requiredPermission - Required permission object {resource, action}
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string} props.redirectPath - Path to redirect to if unauthorized (default: '/login')
 */
const ProtectedRoute = ({ 
  requiredPermission,
  children, 
  redirectPath = '/login' 
}) => {
  const { isAuthenticated, hasPermission } = useAuthStore();
  
  // First check if user is authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectPath} replace />;
  }
  
  // If no specific permission is required, just render children
  if (!requiredPermission) {
    return children;
  }
  
  // Check if user has the required permission
  const { resource, action } = requiredPermission;
  const isAuthorized = hasPermission(resource, action);
  
  if (!isAuthorized) {
    // Return unauthorized access component
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          height: 'calc(100vh - 64px)',
          p: 3
        }}
      >
        <Typography variant="h5" gutterBottom align="center">
          Unauthorized Access
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph align="center">
          You don't have permission to access this resource.
        </Typography>
        <Button 
          variant="contained" 
          
          onClick={() => window.history.back()}
        >
          Go Back
        </Button>
      </Box>
    );
  }
  
  // User is authenticated and authorized
  return children;
};

export default ProtectedRoute;